import { useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { Lock, ShieldCheck } from "lucide-react";
import { useAdminAuth } from "@/hooks/useAdminAuth";

export default function AdminLogin() {
  const { login, isLoggedIn } = useAdminAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Calling navigate() directly during render is unreliable in this react-router
  // version (it warns "call navigate() in a useEffect" and the redirect can silently
  // not take effect) — <Navigate> is the correct declarative way to redirect on render.
  if (isLoggedIn) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  const submit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(email, password);
      navigate("/admin/dashboard", { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || "Could not log in.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-16 text-slate-900">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2.5 text-emerald-700 mb-5">
          <ShieldCheck size={20} />
          <span className="text-xs font-bold uppercase tracking-[0.16em]">Private admin area</span>
        </div>
        <h1 className="font-display text-3xl font-bold mb-2">Log in</h1>
        <p className="text-sm text-slate-600 mb-8">Only the two authorized MySiteCare accounts can access the concept-site listing and dashboard.</p>
        <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5" htmlFor="admin-email">Email</label>
            <input id="admin-email" data-testid="admin-login-email-input" type="email" required autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5" htmlFor="admin-password">Password</label>
            <input id="admin-password" data-testid="admin-login-password-input" type="password" required autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm" />
          </div>
          {error && <p className="text-sm text-rose-600" role="alert" data-testid="admin-login-error">{error}</p>}
          <button data-testid="admin-login-submit-button" type="submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-slate-900 px-5 py-3 text-sm font-bold text-white disabled:opacity-50">
            <Lock size={15} /> {loading ? "Logging in…" : "Log in"}
          </button>
        </form>
        <p className="mt-5 text-center text-sm text-slate-600">Customer or client? <Link to="/account/login" className="font-semibold text-emerald-700 hover:underline" data-testid="admin-login-customer-link">Use customer sign in</Link></p>
      </div>
    </div>
  );
}
